import React, { useState, useEffect } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { useAuth } from '../../context/AuthContext';
import { ticketAPI, userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Ticket, Download, FileText, TrendingUp, PieChart } from 'lucide-react';
import { formatPrice, getMonthKey } from '../../lib/utils';

const MemberDashboard = () => {
  const { user, isMember } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [downloadsRes, statementRes] = await Promise.all([
        userAPI.getDownloads(),
        userAPI.getStatement(getMonthKey()),
      ]);
      
      setStats({
        totalDownloads: downloadsRes.data.length,
        monthlyDownloads: statementRes.data.downloads.length,
        monthlySpend: statementRes.data.total_debits,
        balanceDue: statementRes.data.running_balance,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    {
      title: 'Total Downloads',
      value: stats.totalDownloads,
      icon: Download,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'This Month',
      value: stats.monthlyDownloads,
      icon: Ticket,
      color: 'bg-emerald-100 text-emerald-600',
    },
    {
      title: 'Monthly Spend',
      value: formatPrice(stats.monthlySpend),
      icon: TrendingUp,
      color: 'bg-violet-100 text-violet-600',
    },
    {
      title: 'Balance Due',
      value: formatPrice(stats.balanceDue),
      icon: FileText,
      color: stats.balanceDue > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600',
    },
  ] : [];

  const baseRoute = isMember ? '/member' : '/external';

  return (
    <UserLayout title={`Welcome back, ${user?.name}`} subtitle={isMember ? 'Member Dashboard' : 'External User Dashboard'}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
            {statCards.map((stat, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color.split(' ')[0]}`}>
                      <stat.icon className={`w-6 h-6 ${stat.color.split(' ')[1]}`} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{stat.title}</p>
                      <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <a 
                  href={`${baseRoute}/tickets`}
                  className="p-6 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center card-hover"
                  data-testid="quick-action-tickets"
                >
                  <Ticket className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="font-medium text-slate-700">Browse Tickets</p>
                  <p className="text-sm text-slate-500 mt-1">Find available tickets</p>
                </a>
                <a 
                  href={`${baseRoute}/downloads`}
                  className="p-6 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center card-hover"
                  data-testid="quick-action-downloads"
                >
                  <Download className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="font-medium text-slate-700">My Downloads</p>
                  <p className="text-sm text-slate-500 mt-1">View download history</p>
                </a>
                <a 
                  href={`${baseRoute}/ledger`}
                  className="p-6 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center card-hover"
                  data-testid="quick-action-ledger"
                >
                  <FileText className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="font-medium text-slate-700">Ledger</p>
                  <p className="text-sm text-slate-500 mt-1">Monthly account summary</p>
                </a>
                {isMember && (
                  <a 
                    href={`${baseRoute}/guide-request`}
                    className="p-6 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center card-hover"
                    data-testid="quick-action-guide"
                  >
                    <TrendingUp className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                    <p className="font-medium text-slate-700">Request Guide</p>
                    <p className="text-sm text-slate-500 mt-1">Book a local guide</p>
                  </a>
                )}
                {isMember && (
                  <a 
                    href={`${baseRoute}/investments`}
                    className="p-6 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-colors text-center card-hover"
                    data-testid="quick-action-investments"
                  >
                    <PieChart className="w-8 h-8 text-violet-600 mx-auto mb-2" />
                    <p className="font-medium text-slate-700">My Investments</p>
                    <p className="text-sm text-slate-500 mt-1">View P&L reports</p>
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </UserLayout>
  );
};

export default MemberDashboard;
